<template>
  <div
    v-if="isPurchaseTypeCadence"
    id="edit_cadence">
    <!-- PLAYS PER HOUR -->
    <div
      v-if="hasCadencePurchaseType"
      class="form-group row">
      <form-label
        for-i-d="edit_cadence_plays_per_hour"
        :is-required="true">
        {{ capitalize($t('names.playPerHour', { n: 2 })) }}
      </form-label>
      <div class="col-sm-5">
        <input
          v-if="isEditingPlaysPerHour === false"
          id="edit_cadence_plays_per_hour_formatted"
          type="text"
          :disabled="!canEditCadence"
          :value="cadenceInternalFormatted"
          :class="{ 'form-control': true, input: true }"
          @focus="editPlaysPerHour" />
        <input
          v-show="isEditingPlaysPerHour === true"
          id="edit_cadence_plays_per_hour"
          ref="playsPerHourInput"
          v-model="cadenceInternal"
          type="text"
          name="impressions"
          :disabled="!canEditCadence"
          :class="{ 'form-control': true, input: true }"
          @blur="isEditingPlaysPerHour = false" />
        <span
          v-show="errors.has('cadence')"
          class="help is-danger"
          >{{ errors.first('cadence') }}</span
        >
      </div>

      <div
        v-if="showNotAvailableWarning"
        class="col-sm-1 field-help stick-to-right">
        <div v-tooltip="capitalize($t('messages.playPerHoursNotAvailable'))">
          <icon-warning />
        </div>
      </div>
    </div>
    <div class="form-group row">
      <form-label for-i-d="edit_cadence_smooth_catchup">
        {{ capitalize($t('names.smoothCatchup', { n: 1 })) }}
      </form-label>
      <div class="col-sm-8">
        <toggle-button
          :color="colors.success"
          :sync="true"
          :value="cadenceSmoothCatchupInternal"
          :labels="{
            checked: capitalize(i18n.global.t('actions.yes')),
            unchecked: capitalize(i18n.global.t('actions.no')),
          }"
          :disabled="!canEditCadence"
          @change="cadenceSmoothCatchUpChanged" />
      </div>
      <div class="col-sm-1 field-help">
        <div v-tooltip="capitalize($t('messages.cadenceSmoothCatchupTooltip'))">
          <icon-info />
        </div>
      </div>
    </div>

    <modal
      id="checking-capacity-modal"
      class="cadence-modal"
      :visible="isCheckingCapacityModalVisible"
      :title="capitalize($t('messages.checkCapacityTitle'))"
      :body="capitalize($t('messages.checkCapacityMessage'))"
      :button1-caption="capitalize($t('actions.continue'))"
      @cancelled="isCheckingCapacityModalVisible = false"
      @confirmed="checkCapacity" />

    <modal
      id="cadence-warning-modal"
      class="cadence-modal"
      :visible="isCadenceWarningModalVisible"
      :title="warningModalTitle"
      :body="warningModalBody"
      :button1-caption="capitalize($t('actions.iUnderstand'))"
      :button2-caption="$t('actions.cancel')"
      :button2-visible="true"
      @cancelled="isCadenceWarningModalVisible = false"
      @confirmed="acceptWarnings" />
  </div>
</template>

<script>
  import { DateTime } from 'luxon';
  import numbro from 'numbro';
  import { capitalize } from 'vue';
  import { mapGetters, mapState } from 'vuex';

  import colors from '@/Colors';
  import { FormLabel, ToggleButton } from '@/common/components/controls';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import PurchaseTypes from '@/common/enums/purchase-types.enum';
  import { IconInfo, IconWarning } from '@/common/icons/monochrome';
  import formatNumber from '@/utils/NumberFormatter';
  import UserNotifications from '@/utils/UserNotifications';
  import MessageCollection from '@/validator/MessageCollection';
  import i18n from '@/vueI18n';

  const WarningNotificationEnum = {
    CAPACITY_CHECK_REQUIRED: 'CAPACITY MUST BE CHECKED',
    CADENCE_WITH_UNRESERVED_IMPRESSIONS: 'CAN BE REACHED WITH RESERVED AND UNRESERVED IMPRESSIONS',
    CHECK_GUARANTEED_AVAILS: 'UNCHECKED AVAILS',
    CADENCE_LACKS_AVAILABILITY: 'NOT FEASIBLE',
    CANNOT_MAINTAIN_CADENCE:
      'HISTORICAL CADENCE DATA SHOWS THAT ONE OR MORE UNITS CANNOT MAINTAIN THE CADENCE. TRY SOMETHING BELOW',
  };

  export default {
    name: 'EditCadence',
    components: {
      FormLabel,
      IconInfo,
      IconWarning,
      ToggleButton,
    },
    props: {
      cadence: {
        type: Number,
      },
      originalCadence: {
        type: Number,
      },
      cadenceSmoothCatchup: {
        type: Boolean,
      },
      budgetMode: {
        type: String,
      },
      canEditCadence: {
        type: Boolean,
        default: false,
      },
      purchaseType: {
        type: String,
      },
      budget: {
        type: [Number, String],
        default: 0,
      },
      impressions: {
        type: Number,
        default: 0,
      },
      estimatedTargetImpressions: {
        type: Number,
      },
      updateCadence: {
        // Store action
        type: Function,
      },
      updateBudget: {
        // Store action
        type: Function,
      },
      updateImpressions: {
        // Store action
        type: Function,
      },
      updateCadenceSmoothCatchup: {
        // Store action
        type: Function,
      },
      minCadence: {
        type: Number,
        default: null,
      },
      isNew: {
        type: Boolean,
      },
      isCheckingAvails: {
        type: Boolean,
        default: false,
      },
      onePlayPerHourDemographicImpressions: {
        type: Number,
        default: null,
      },
      audienceImpressionsMultiplier: {
        type: Number,
        default: 1,
      },
      availsResult: {
        type: Object,
        default: null,
      },
      targetParams: {
        type: Object,
        default: null,
      },
      isSaving: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        cadenceInternal: this.cadence,
        availsResultLastChangedOn: null,
        cadenceSmoothCatchupInternal: false,
        colors,
        customCadence: 1,
        errors: new MessageCollection(),
        hasCadenceCannotBeMaintained: false,
        hasClickedIUnderstand: false,
        hasNotAvailableCadence: false,
        hasUnreservedImpressionsCadence: false,
        i18n,
        isCheckingCapacityModalVisible: false,
        isCadenceWarningModalVisible: false,
        isEditingPlaysPerHour: false,
        isReady: false,
        maxHistoricalCadence: null,
        notificationTimers: [],
        optimizedCadence: null,
        sampleCadenceForLoader: [...Array(3)].map((x, i) => i + 1),
        showNotAvailableWarning: false,
        targetingLastChangedOn: null,
        shouldCheckCapacity: false,
        userChangedImpressions: false,
        userChangedBudget: false,
        userChangedBudgetMode: false,
        userChangedCadence: false,
        warningModalBody: '',
        warningModalTitle: '',
        warningNotifications: [
          {
            titleKey: 'messages.checkCapacityTitle',
            bodyKey: 'messages.capacityCheckRequiredWarning',
            visible: false,
            type: WarningNotificationEnum.CAPACITY_CHECK_REQUIRED,
          },
          {
            titleKey: 'messages.cadenceWithUnreservedImpressions',
            bodyKey: 'messages.lineItemCadenceGoalReachedWithUnreservedImpressions',
            visible: false,
            type: WarningNotificationEnum.CADENCE_WITH_UNRESERVED_IMPRESSIONS,
          },
          {
            titleKey: 'messages.cadenceLackAvailabilities',
            bodyKey: 'messages.lineItemCadenceGoalCannotBeReached',
            visible: false,
            type: WarningNotificationEnum.CADENCE_LACKS_AVAILABILITY,
          },
          {
            titleKey: 'messages.cannotMaintainCadenceTitle',
            bodyKey: 'messages.cannotMaintainCadenceBody',
            visible: false,
            type: WarningNotificationEnum.CANNOT_MAINTAIN_CADENCE,
          },
        ],
        BudgetModes,
      };
    },
    computed: {
      ...mapGetters('orgApp', ['isDemandOrganization']),
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
      }),
      cadenceInternalFormatted() {
        return formatNumber(this.cadenceInternal, '0,0.00');
      },
      hasCadencePurchaseType() {
        return this.purchaseType === PurchaseTypes.CADENCE;
      },
      goal() {
        return this.budgetMode === BudgetModes.BUDGET ? this.budget * 1 : this.impressions * 1;
      },
      isBudgetModeAmount() {
        return this.budgetMode === BudgetModes.BUDGET;
      },
      isBudgetModeImpressions() {
        return this.budgetMode === BudgetModes.IMPRESSIONS;
      },
      isPurchaseTypeCadence() {
        return this.purchaseType === PurchaseTypes.CADENCE;
      },
      cadenceEstimatedAudienceImpressions() {
        return Math.round(
          this.onePlayPerHourDemographicImpressions * this.cadenceInternal * this.audienceImpressionsMultiplier,
        );
      },
      shouldShowUnreservedImpressionsWarning() {
        // For organizations that use HiveStack avails provider, don't show unreserved imps warning because there are none.
        return (
          this.organizationSettings !== null &&
          this.organizationSettings.externalAvailsUrl !== 'full-adserver-availability'
        );
      },
      maxCadence() {
        return {
          plays: formatNumber(this.availsResult?.maxCadenceAvailable, '0, 0.00'),
          impressions: formatNumber(this.availsResult?.maxCadenceImpressionsAvailable, '0, 0'),
        };
      },
      shouldShowWarnings() {
        if ((this.isNew || this.originalCadence === null) && this.availsResult == null) return false;
        if (
          this.userChangedImpressions ||
          this.userChangedBudget ||
          this.userChangedCadence ||
          this.userChangedBudgetMode
        )
          return true;
        return this.availsResultLastChangedOn <= this.targetingLastChangedOn;
      },
    },
    watch: {
      cadence() {
        this.cadenceInternal = this.cadence;
      },
      cadenceInternal(value, previousValue) {
        if (!this.canEditCadence) {
          return;
        }
        if (value === previousValue) {
          return;
        }

        this.userChangedCadence = true;
        this.shouldCheckCapacity = true;
        this.showCapacityCheckRequiredWarning();

        const formattedValue = String(value).replace(/,/g, ''); // to remove commas from the value
        const number = value === '' || value == null || Number.isNaN(value) ? 0 : parseFloat(formattedValue);
        const roundedNumber = Math.round(number * 100) / 100;

        this.updateCadence(roundedNumber);

        this.updateImpressions(this.cadenceEstimatedAudienceImpressions);

        this.$emit('changed', value);
      },
      availsResult(val) {
        if (val) this.availsResultLastChangedOn = DateTime.local();

        if (this.hasCadencePurchaseType) {
          this.acceptWarnings();
          this.shouldCheckCapacity = false;
          this.showNotAvailableWarning = val.checkAvailsResult !== 'AVAILABLE';

          if (this.isBudgetModeAmount) {
            const optimizedImpressions = numbro(this.availsResult?.optimizedImpressions?.toFixed(2))?.value();
            this.updateImpressions(optimizedImpressions);
          }
          // TODO - Should we update cadence to closest available ?
          // this.cadenceInternal = parseFloat(this.availsResult.closestCadenceAvailable);

          this.detectCadenceWarnings();
        }
      },

      budget() {
        if (this.isBudgetModeAmount) {
          this.userChangedBudget = true;
          this.shouldCheckCapacity = true;
          this.showCapacityCheckRequiredWarning();
        }
      },
      budgetMode(val, previousVal) {
        this.clearCadenceStateFlags();
        this.detectCadenceWarnings();
      },
      impressions() {
        if (this.isBudgetModeImpressions) {
          this.userChangedImpressions = true;
          this.shouldCheckCapacity = true;
          this.showCapacityCheckRequiredWarning();
        }
      },
      isCheckingAvails(val) {
        if (val) {
          this.isCheckingCapacityModalVisible = false;
          this.isCadenceWarningModalVisible = false;
          this.shouldCheckCapacity = false;
        }
      },

      purchaseType(val) {
        if (val === PurchaseTypes.CADENCE) {
          this.resetCadence();
          if (this.shouldCheckCapacity) {
            this.shouldCheckCapacity = false;
          }
        }
      },
      targetParams(val, previousVal) {
        if (previousVal == null || !this.isReady) return; // Not ready.
        if (JSON.stringify(val) === JSON.stringify(previousVal)) return; // No change.
        this.shouldCheckCapacity = true;
        this.showCapacityCheckRequiredWarning();
        this.targetingLastChangedOn = DateTime.local();
      },
      cadenceEstimatedAudienceImpressions(value) {
        if (!this.shouldCheckCapacity) {
          return;
        }
        this.updateImpressions(value);
      },
      cadenceSmoothCatchup() {
        this.cadenceSmoothCatchupInternal = this.cadenceSmoothCatchup;
      },
    },
    mounted() {
      if (this.isNew) {
        if (this.minCadence != null) {
          this.cadenceInternal = this.minCadence;
        }
        this.cadenceSmoothCatchupInternal = true;
        this.updateCadenceSmoothCatchup(true);
        this.shouldCheckCapacity = false;
      } else {
        this.cadenceSmoothCatchupInternal = this.cadenceSmoothCatchup;
      }

      this.isReady = true;
    },
    methods: {
      capitalize,
      formatNumber,
      cadenceSmoothCatchUpChanged(value) {
        this.cadenceSmoothCatchupInternal = value;
        this.updateCadenceSmoothCatchup(value);
        if (value) {
          this.shouldCheckCapacity = this.cadenceSmoothCatchup;
        }
        this.hideWarningNotifications();
      },
      editPlaysPerHour() {
        this.isEditingPlaysPerHour = true;
        setTimeout(() => {
          this.$refs.playsPerHourInput.focus();
        }, 100);
      },
      displayWarningBasedOnFeasibility(feasibility) {
        if (feasibility === WarningNotificationEnum.CADENCE_LACKS_AVAILABILITY) {
          this.displayCadenceNotAvailablePopUp();
        } else if (feasibility === WarningNotificationEnum.CADENCE_WITH_UNRESERVED_IMPRESSIONS) {
          this.displayCadenceHasUnreservedImpressions();
        } else if (feasibility === WarningNotificationEnum.RESERVED_IMPRESSIONS) {
          // This enum does not exist...
          this.clearCadenceStateFlags();
        } else if (feasibility.startsWith(WarningNotificationEnum.CANNOT_MAINTAIN_CADENCE)) {
          this.maxHistoricalCadence = +parseFloat(feasibility.match(/(\d*\.\d+)/)).toFixed(2);
          this.displayCadenceCannotBeMaintained();
        }
      },
      detectCadenceWarnings() {
        if (
          this.availsResult === null ||
          this.availsResult.cadenceRange === null ||
          this.availsResult.floorCadenceFeasibility === null ||
          this.availsResult.optimizedCadenceFeasibility === null ||
          this.availsResult.ceilCadenceFeasibility === null ||
          this.availsResult.maxCadenceFeasibility === null ||
          this.availsResult.closestCadenceAvailable === null ||
          (this.availsResult.optimizedImpressions !== null &&
            !this.cadenceSmoothCatchup &&
            this.optimizedCadence === this.customCadence) ||
          this.cadenceSmoothCatchup
        ) {
          return;
        }
        this.hasClickedIUnderstand = false;

        if (this.$refs?.editCadence?.isOptimizedCadence) {
          this.displayWarningBasedOnFeasibility(this.availsResult.optimizedCadenceFeasibility);
        }

        const floorCadence = this.availsResult.cadenceRange[0];
        const ceilCadence = this.availsResult.cadenceRange[1];

        if (this.cadence === floorCadence) {
          this.displayWarningBasedOnFeasibility(this.availsResult.floorCadenceFeasibility);
        } else if (this.cadence === this.availsResult.closestCadenceAvailable) {
          this.displayWarningBasedOnFeasibility(this.availsResult.optimizedCadenceFeasibility);
        } else if (this.cadence === ceilCadence) {
          this.displayWarningBasedOnFeasibility(this.availsResult.ceilCadenceFeasibility);
        } else if (this.cadence === this.availsResult.maxCadenceAvailable) {
          this.displayWarningBasedOnFeasibility(this.availsResult.maxCadenceFeasibility);
        } else if (this.cadence > this.availsResult.maxCadenceAvailable) {
          this.displayCadenceNotAvailablePopUp();
        } else if (this.cadence > ceilCadence) {
          this.displayWarningBasedOnFeasibility(this.availsResult.maxCadenceFeasibility);
        } else if (this.cadence > this.availsResult.closestCadenceAvailable) {
          this.displayWarningBasedOnFeasibility(this.availsResult.ceilCadenceFeasibility);
        } else if (this.cadence > floorCadence) {
          this.displayWarningBasedOnFeasibility(this.availsResult.optimizedCadenceFeasibility);
        } else {
          this.displayWarningBasedOnFeasibility(this.availsResult.floorCadenceFeasibility);
        }
      },
      scenarioIsValidToSave() {
        // buyers can't run check avails
        if (!this.canEditCadence && this.isDemandOrganization) {
          return true; // If cadence cannot be edited, don't show warning or avails check related to cadence, because the user cannot modify it anyway
        }
        if (this.hasCadencePurchaseType) {
          if (this.shouldCheckCapacity) {
            this.hideWarningNotifications();
            this.isCheckingCapacityModalVisible = true;
            return false;
          }

          if (this.hasCadenceWarnings() && !this.hasClickedIUnderstand) {
            this.displayCadenceWarningModal();
            return false;
          }
        }
        return true;
      },
      displayCadenceWarningModal() {
        if (!this.hasCadenceWarnings()) {
          // Make sure we don't display the modal if there isn't any warning
          return;
        }

        if (this.shouldShowUnreservedImpressionsWarning && this.hasUnreservedImpressionsCadence) {
          this.warningModalTitle = i18n.global.t('messages.cadenceWithUnreservedImpressions');
          this.warningModalBody = i18n.global.t('messages.lineItemCadenceGoalReachedWithUnreservedImpressions');
        }

        if (this.hasNotAvailableCadence) {
          this.warningModalTitle = i18n.global.t('messages.cadenceLackAvailabilities');
          this.warningModalBody = i18n.global.t('messages.lineItemCadenceGoalCannotBeReached');
        }

        if (this.hasCadenceCannotBeMaintained) {
          this.warningModalTitle = i18n.global.t('messages.cannotMaintainCadenceTitle');
          this.warningModalBody = i18n.global.t('messages.cannotMaintainCadenceBody', {
            cadence: this.maxHistoricalCadence,
          });
        }

        this.isCadenceWarningModalVisible = true;
      },
      acceptWarnings() {
        this.userChangedImpressions = false;
        this.userChangedBudget = false;
        this.userChangedCadence = false;

        this.hideWarningNotifications();
        this.hasClickedIUnderstand = true;
      },
      checkCapacity() {
        this.$emit('checkCapacity');
      },
      clearCadenceStateFlags() {
        this.hasNotAvailableCadence = false;
        this.hasUnreservedImpressionsCadence = false;
        this.hasClickedIUnderstand = false;
        this.hasCadenceCannotBeMaintained = false;
      },
      displayCadenceNotAvailablePopUp() {
        this.showWarning(WarningNotificationEnum.CADENCE_LACKS_AVAILABILITY, () => {
          this.hasNotAvailableCadence = true;
        });
      },
      displayCadenceHasUnreservedImpressions() {
        this.showWarning(WarningNotificationEnum.CADENCE_WITH_UNRESERVED_IMPRESSIONS, () => {
          this.hasUnreservedImpressionsCadence = true;
        });
      },

      displayCadenceCannotBeMaintained() {
        this.showWarning(WarningNotificationEnum.CANNOT_MAINTAIN_CADENCE, () => {
          this.hasCadenceCannotBeMaintained = true;
        });
      },
      showCapacityCheckRequiredWarning() {
        this.showWarning(WarningNotificationEnum.CAPACITY_CHECK_REQUIRED);
      },
      showWarning(type, cadenceStateFlagUpdateFunction) {
        if (this.isSaving) {
          return; // Don't show warnings while the LI is being saved because LI values can change during the LI saving pre-process and show unwanted warning messages
        }
        if (!this.shouldShowWarnings) return;

        // Hide the popup after 20 seconds.
        if (!this.getWarningVisibility(type)) {
          this.hideWarningNotifications();
          this.setWarningVisibility(type, true);
          const notification = this.warningNotifications.find((x) => x.type === type);
          UserNotifications.warning({
            title: i18n.global.t(notification.titleKey),
            message: i18n.global.t(notification.bodyKey),
            category: 'CADENCE',
            actions: [
              {
                label: this.capitalize(i18n.global.t('actions.iUnderstand')),
                isDismissingNotification: true,
                fn: this.acceptWarnings,
              },
            ],
            duration: 20000,
          });

          this.clearCadenceStateFlags();
          if (cadenceStateFlagUpdateFunction) {
            cadenceStateFlagUpdateFunction();
          }
        }
        const timer = setTimeout(() => {
          this.setWarningVisibility(type, false);
          this.notificationTimers = this.notificationTimers.filter((t) => t !== timer);
        }, 20000);
        this.notificationTimers.push(timer);
      },
      hasCadenceWarnings() {
        return (
          this.hasCadencePurchaseType &&
          ((this.shouldShowUnreservedImpressionsWarning && this.hasUnreservedImpressionsCadence) ||
            this.hasNotAvailableCadence ||
            this.hasCadenceCannotBeMaintained ||
            this.shouldCheckCapacity)
        );
      },
      resetCadence() {
        this.customCadence = 1;

        this.clearCadenceStateFlags();
      },
      hideWarningNotifications() {
        UserNotifications.clear('CADENCE');
        this.isCadenceWarningModalVisible = false;

        this.warningNotifications.forEach((n) => {
          this.setWarningVisibility(n.type, false);
        });

        this.notificationTimers.forEach((t) => clearTimeout(t));
        this.notificationTimers = [];
      },
      getWarningVisibility(warningType) {
        return this.warningNotifications.find((n) => n.type === warningType).visible;
      },
      setWarningVisibility(warningType, isVisible) {
        const notification = this.warningNotifications.find((n) => n.type === warningType);
        notification.visible = isVisible;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .main-content {
      .optimized-cadence-loader {
        min-width: 3rem;
      }

      #compute-cadence-slot {
        display: flex;
        flex-direction: row;
        align-items: center;
        // justify-content: center;
        height: 6rem;
        border-radius: 0.5rem;
        padding: 0.8rem 0 0.8rem 1.2rem;
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 1rem;

        .icon {
          width: 4rem;
          height: 4rem;
          margin-right: 2rem;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          border-radius: 2rem;

          background-color: #fff;
          color: var(--primaryColor);

          svg {
            display: block;
            margin: auto;
            width: 2.4rem;
            height: 2.4rem;

            stroke: var(--primaryColor);
            fill: #fff;
          }
        }

        background-color: var(--primaryColor);
        color: #fff;

        .btn-no-borders {
          border: none;
          background: none;
          cursor: pointer;
          box-shadow: none;

          svg {
            width: 2rem;
            height: 2rem;
            fill: #fff;
            stroke: #fff;
          }

          span {
            color: #fff;
          }
        }

        .refresh-button {
          padding: 0.5rem;
          margin-left: 1.5rem;

          background-color: #fff;
          color: var(--primaryColor);

          svg {
            width: 1.2rem;
            height: 1.2rem;
            margin-left: 0.5rem;

            stroke: var(--primaryColor);
            fill: var(--primaryColor);
          }
        }

        .font-light {
          font-weight: 200;
          font-size: small;
        }

        .compute-cadence-btn {
          white-space: break-spaces;
        }

        .spacer {
          flex-grow: 1;
        }
      }

      #cadence-options-btn {
        display: flex;
        margin-top: 1rem;

        .btn-default {
          padding: 0.5rem;

          span {
            font-size: smaller;
          }
        }

        svg {
          width: 1rem;
          height: 1rem;
          margin-left: 1rem;
          color: #999;
        }
      }

      .cadence-drop-down-popup-loader-container {
        height: 21rem;
        padding: 1rem;
        box-shadow: $small-shadow;
      }

      .cadence-drop-down-popup-loader-header {
        padding: 0 1.2rem 1rem;
      }

      .cadence-drop-down-popup-container {
        .cadence-drop-down-popup-content {
          padding: 1rem 1rem 0.2rem 1rem;
          box-shadow: $small-shadow;
          font-size: 0.9em;

          label {
            font-size: 0.9em;
            margin-bottom: 0.3rem;
          }

          ul {
            list-style: none;
            padding-left: 0.2rem;
            width: 100%;

            li {
              display: flex;
              align-items: center;
              margin-bottom: 0.2rem;
              list-style: none;
              padding: 1rem;
              cursor: pointer;
              border: 0.1rem solid transparent;

              .icon {
                width: 4rem;
                min-height: 4rem;
                margin-right: 2rem;
                display: inline-flex;
                justify-content: center;
                align-items: center;
                border-radius: 2rem;

                background-color: var(--primaryColor);
                color: #fff;

                svg {
                  display: block;
                  margin: auto;
                  width: 2.4rem;
                  height: 2.4rem;
                  stroke: #fff;
                  fill: #fff;
                }
              }

              .spacer {
                flex-grow: 1;
              }

              .arrow {
                fill: var(--primaryColor);
                stroke: var(--primaryColor);

                opacity: 0;
                height: 4rem;
                width: 4rem;
                display: flex;
                align-items: center;
              }

              .values {
                display: flex;
                flex-direction: column;

                .bold {
                  font-weight: 700;
                }
              }

              &:hover {
                background-color: #f2f2f2;
                border: 0.1rem solid rgba(0, 0, 0, 0.15);
                box-shadow: $small-shadow;

                .arrow {
                  opacity: 1;
                }
              }

              &.active {
                background: var(--primaryColor);
                color: var(--primaryColorReverse);

                .icon {
                  background-color: #fff;

                  svg {
                    fill: #fff;
                    stroke: var(--primaryColor);
                  }
                }

                .arrow {
                  fill: #fff;
                  stroke: #fff;
                }
              }
            }
          }
        }
      }
    }

    #checking-capacity-modal {
      &.modal {
        height: 100%;
        width: 100%;
        padding-top: calc(100vh / 2 - 20rem);
      }
    }
  }
</style>
